import React, { useMemo, useState } from "react";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Table } from "antd";
import CcButton from "../../CoilConfigurator/components/CcButton";
import OpStatusPill from "../components/OpStatusPill";
import type { ConfigurationResult } from "../models/types";

interface ConfigurationsDashboardProps {
  rows: ConfigurationResult[];
  loading: boolean;
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

type ColumnFilterKey = "reference" | "project" | "client" | "steelOd" | "coating";

const EMPTY_FILTERS: Record<ColumnFilterKey, string> = {
  reference: "",
  project: "",
  client: "",
  steelOd: "",
  coating: "",
};

const formatSteelOd = (row: ConfigurationResult) => {
  const value = row.pipe.outsideDiameter?.trim();
  if (!value) return "—";
  return `${value} ${row.pipe.outsideDiameterUnit || "mm"}`.trim();
};

const formatCoating = (row: ConfigurationResult) => {
  const value = row.process.coatingThickness?.trim();
  if (!value) return "—";
  return `${value} ${row.process.coatingThicknessUnit || "mm"}`.trim();
};

const formatBlast = (row: ConfigurationResult) => {
  if (!row.blastFrame) return "—";
  const length = row.blastFrame.lengthCategory ? ` · L${row.blastFrame.lengthCategory}` : "";
  return `Ø${row.blastFrame.diameterCategory}${length}`;
};

const formatCoil = (row: ConfigurationResult) => {
  if (!row.inductionCoil) return "—";
  const length = row.inductionCoil.lengthCategory ? ` · L${row.inductionCoil.lengthCategory}` : "";
  return `Ø${row.inductionCoil.diameterCategory}${length}`;
};

const ConfigurationsDashboard = ({
  rows,
  loading,
  onOpen,
  onEdit,
  onDelete,
}: ConfigurationsDashboardProps) => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      const reference = `${row.project.projectNumber} ${row.configurationId}`.toLowerCase();
      const project = row.project.projectName.toLowerCase();
      const client = row.project.customer.toLowerCase();
      const steelOd = formatSteelOd(row).toLowerCase();
      const coating = formatCoating(row).toLowerCase();

      if (filters.reference && !reference.includes(filters.reference.trim().toLowerCase())) {
        return false;
      }
      if (filters.project && !project.includes(filters.project.trim().toLowerCase())) return false;
      if (filters.client && !client.includes(filters.client.trim().toLowerCase())) return false;
      if (filters.steelOd && !steelOd.includes(filters.steelOd.trim().toLowerCase())) return false;
      if (filters.coating && !coating.includes(filters.coating.trim().toLowerCase())) return false;
      return true;
    });
  }, [filters, rows]);

  const filterInput = (key: ColumnFilterKey) => (
    <input
      className="op-column-filter"
      type="search"
      placeholder="Search…"
      value={filters[key]}
      onChange={(event) => setFilters((prev) => ({ ...prev, [key]: event.target.value }))}
      aria-label={`Filter ${key}`}
    />
  );

  return (
    <div className="op-dashboard">
      <div className="op-view-title-row">
        <div className="op-view-title-row__left">
          <h2 className="op-section-title">List of Configurations</h2>
          <span className="op-count-pill">
            {filtered.length} {filtered.length === 1 ? "item" : "items"}
          </span>
        </div>
      </div>

      <Table
        className="op-table op-table--spec"
        loading={loading}
        rowKey="configurationId"
        dataSource={filtered}
        pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ["5", "10", "20"] }}
        locale={{ emptyText: "No configurations yet. Create one to get started." }}
        columns={[
          {
            title: (
              <div className="op-th-stack">
                <span>Reference</span>
                {filterInput("reference")}
              </div>
            ),
            key: "reference",
            render: (_: unknown, row: ConfigurationResult) => (
              <button
                type="button"
                className="op-reference-link"
                onClick={() => onOpen(row.configurationId)}
              >
                {row.project.projectNumber || row.configurationId}
              </button>
            ),
          },
          {
            title: (
              <div className="op-th-stack">
                <span>Project</span>
                {filterInput("project")}
              </div>
            ),
            key: "project",
            render: (_: unknown, row: ConfigurationResult) => row.project.projectName || "—",
          },
          {
            title: (
              <div className="op-th-stack">
                <span>Client</span>
                {filterInput("client")}
              </div>
            ),
            key: "client",
            render: (_: unknown, row: ConfigurationResult) => row.project.customer || "—",
          },
          {
            title: (
              <div className="op-th-stack">
                <span>Steel OD</span>
                {filterInput("steelOd")}
              </div>
            ),
            key: "steelOd",
            render: (_: unknown, row: ConfigurationResult) => formatSteelOd(row),
          },
          {
            title: (
              <div className="op-th-stack">
                <span>Coating build</span>
                {filterInput("coating")}
              </div>
            ),
            key: "coating",
            render: (_: unknown, row: ConfigurationResult) => formatCoating(row),
          },
          {
            title: "Blast Frame",
            key: "blastFrame",
            render: (_: unknown, row: ConfigurationResult) => formatBlast(row),
          },
          {
            title: "Induction Coil",
            key: "inductionCoil",
            render: (_: unknown, row: ConfigurationResult) => formatCoil(row),
          },
          {
            title: "Status",
            key: "status",
            render: (_: unknown, row: ConfigurationResult) => <OpStatusPill status={row.status} />,
          },
          {
            title: "Actions",
            key: "actions",
            width: 136,
            render: (_: unknown, row: ConfigurationResult) => (
              <div className="op-row-actions">
                <CcButton
                  variant="icon"
                  aria-label={`View ${row.configurationId}`}
                  onClick={() => onOpen(row.configurationId)}
                >
                  <EyeOutlined />
                </CcButton>
                <CcButton
                  variant="icon"
                  aria-label={`Edit ${row.configurationId}`}
                  onClick={() => onEdit(row.configurationId)}
                >
                  <EditOutlined />
                </CcButton>
                <CcButton
                  variant="icon"
                  danger
                  aria-label={`Delete ${row.configurationId}`}
                  onClick={() => onDelete(row.configurationId)}
                >
                  <DeleteOutlined />
                </CcButton>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default ConfigurationsDashboard;
